import { useEffect, useMemo, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { CalendarDatePicker } from '../../../../components/ERP/Calendar/Calendar'
import ErpPeriodDropdown from '../../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import TimePicker from '../../../../components/ERP/TimePicker/TimePicker'
import { patientService } from '../../../../services/patientApi'
import { formatDay, nowTimeInput, todayInput } from '../patientFormat'
import { TOLERANCE_OPTIONS, blankCheck, doseLabel, formatClock, painClass } from './therapyFormat'

function TherapySessionForm({ patientId, activities, sessions, archived, onSaved }) {
  const active = useMemo(
    () => activities.filter((activity) => activity.status === 'ativa'),
    [activities]
  )
  const [date, setDate] = useState(todayInput())
  const [time, setTime] = useState(nowTimeInput())
  const [painScore, setPainScore] = useState(0)
  const [painLocation, setPainLocation] = useState('')
  const [tolerance, setTolerance] = useState('boa')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState({})
  const [repeatedFrom, setRepeatedFrom] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setItems((current) => {
      const next = {}
      active.forEach((activity) => {
        next[activity.id] = current[activity.id] || blankCheck(activity.id)
      })
      return next
    })
  }, [active])

  const updateItem = (activityId, patch) => {
    setRepeatedFrom('')
    setItems((current) => ({
      ...current,
      [activityId]: { ...(current[activityId] || blankCheck(activityId)), ...patch }
    }))
  }

  const repeatLast = () => {
    const last = sessions[0]
    if (!last) return
    const previous = new Map((last.items || []).map((item) => [Number(item.activity_id), item]))
    const next = {}
    active.forEach((activity) => {
      const item = previous.get(Number(activity.id))
      next[activity.id] = item ? {
        activityId: activity.id,
        completed: Boolean(item.completed),
        setsDone: item.sets_done ?? '',
        repetitionsDone: item.repetitions_done ?? '',
        notes: item.notes || ''
      } : blankCheck(activity.id)
    })
    setItems(next)
    const clock = formatClock(last.session_time)
    setRepeatedFrom(`${formatDay(last.session_date)}${clock ? ` às ${clock}` : ''}`)
  }

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await patientService.addTherapySession(patientId, {
        date,
        time,
        painScore,
        painLocation: painLocation.trim(),
        tolerance,
        notes: notes.trim(),
        items: active.map((activity) => {
          const item = items[activity.id] || blankCheck(activity.id)
          return {
            activityId: activity.id,
            completed: Boolean(item.completed),
            setsDone: item.setsDone === '' ? null : Number(item.setsDone),
            repetitionsDone: item.repetitionsDone === '' ? null : Number(item.repetitionsDone),
            notes: item.notes
          }
        })
      })
      setNotes('')
      setPainLocation('')
      setPainScore(0)
      setTolerance('boa')
      setRepeatedFrom('')
      setItems(Object.fromEntries(active.map((activity) => [activity.id, blankCheck(activity.id)])))
      onSaved()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível registrar a sessão')
    } finally {
      setSaving(false)
    }
  }

  if (archived) return null

  return (
    <section className="therapy-block" data-onboarding="therapy-session">
      <div className="patients-section-head">
        <div>
          <h3>Registro da sessão</h3>
          <p className="patients-muted">O que foi realizado hoje, a dor e a tolerância do paciente.</p>
        </div>
        <button
          type="button"
          className="patients-btn patients-btn--ghost"
          onClick={repeatLast}
          disabled={!sessions.length || !active.length}
        >
          <RotateCcw size={14} /> Repetir última sessão
        </button>
      </div>
      <form onSubmit={submit} className="patients-panel therapy-session">
        {error && <p className="patients-alert-strip">{error}</p>}
        {repeatedFrom && <p className="patients-note">Checklist preenchido com a sessão de {repeatedFrom}.</p>}
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-evolution-date">
            <label>Data</label>
            <CalendarDatePicker value={date} onChange={setDate} title="Data da sessão" placeholder="Selecionar data" />
          </div>
          <div className="patients-evolution-time">
            <label>Hora</label>
            <TimePicker value={time} onChange={setTime} title="Hora da sessão" placeholder="Selecionar hora" />
          </div>
        </div>
        <div className="therapy-pain-row">
          <div>
            <span className="therapy-label">Dor (0 a 10)</span>
            <div className="therapy-pain" role="radiogroup" aria-label="Nível de dor">
              {Array.from({ length: 11 }, (_, score) => (
                <button
                  key={score}
                  type="button"
                  className={`therapy-pain-score ${painClass(score)}${painScore === score ? ' is-selected' : ''}`}
                  aria-pressed={painScore === score}
                  onClick={() => setPainScore(score)}
                >
                  {score}
                </button>
              ))}
            </div>
          </div>
          <label className="therapy-location">
            <span className="therapy-label">Local da dor</span>
            <input
              value={painLocation}
              onChange={(event) => setPainLocation(event.target.value)}
              placeholder="Ex.: Lombar, ombro direito"
              maxLength={120}
            />
          </label>
        </div>
        <div className="therapy-location">
          <span className="therapy-label">Tolerância</span>
          <ErpPeriodDropdown
            className="patients-field-dropdown"
            label=""
            align="left"
            portal
            ariaLabel="Tolerância"
            value={tolerance}
            options={TOLERANCE_OPTIONS}
            onChange={setTolerance}
          />
        </div>
        {active.length === 0 && (
          <p className="patients-muted">Não há atividades ativas no plano. A sessão ainda pode registrar dor e tolerância.</p>
        )}
        <div className="therapy-checklist">
          {active.map((activity) => {
            const item = items[activity.id] || blankCheck(activity.id)
            return (
              <article key={activity.id} className={`therapy-check${item.completed ? ' is-done' : ''}`}>
                <label className="therapy-check-main">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={(event) => updateItem(activity.id, { completed: event.target.checked })}
                  />
                  <span>
                    <strong>{activity.title}</strong>
                    <small>{doseLabel(activity)}</small>
                  </span>
                </label>
                <div className="therapy-check-fields">
                  <label>
                    <span>Séries</span>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={item.setsDone}
                      onChange={(event) => updateItem(activity.id, { setsDone: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Repetições</span>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={item.repetitionsDone}
                      onChange={(event) => updateItem(activity.id, { repetitionsDone: event.target.value })}
                    />
                  </label>
                  <label className="therapy-check-notes">
                    <span>Observação</span>
                    <input
                      value={item.notes}
                      onChange={(event) => updateItem(activity.id, { notes: event.target.value })}
                      placeholder="Como o paciente respondeu"
                    />
                  </label>
                </div>
              </article>
            )
          })}
        </div>
        <label className="therapy-location">
          <span className="therapy-label">Observação da sessão</span>
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Intercorrências, fadiga, orientação deixada com o cuidador" />
        </label>
        <div className="patients-upload-actions">
          <button type="submit" className="patients-btn patients-btn--primary" disabled={saving}>
            {saving ? 'Salvando...' : 'Registrar sessão'}
          </button>
        </div>
      </form>
    </section>
  )
}

export default TherapySessionForm
